import React, { FC } from "react";

import log from "loglevel";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocalStorage } from "react-use";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Checkbox, FormControlLabel, InputAdornment } from "@mui/material";

import { EmailIcon } from "@/assets/icons";
import { ItsMeButton, OrSeparator, TextInput } from "@/components/atoms";
import { PasswordInput } from "@/components/molecules";
import { auth } from "@/shared/apis/userApi";
import { useCookieConsent } from "@/shared/contexts";
import { ForbiddenError } from "@/shared/errors";
import { useNavigation } from "@/shared/hooks";

import { legacyPasswordValidationSchema, PasswordFormType } from "../../../shared";
import { StyledAuthForm } from "../LoginCheckEmail/styled";

type LoginAuthenticatedProps = {
    userStatus: MyBrokerApi.UserStatus;
    handleItsmeLogin: () => void;
    onAuthenticate: () => void;
};

export interface LoginAuthenticatedData extends PasswordFormType {
    email: string;
    rememberEmail: boolean;
}

const LoginAuthenticated: FC<LoginAuthenticatedProps> = ({ userStatus, handleItsmeLogin, onAuthenticate }) => {
    const { navToResetPassword } = useNavigation();
    const { t } = useTranslation();
    const { categories: cookieConsentCategories } = useCookieConsent();
    const [rememberEmail, storeRememberEmail, clearRememberMe] = useLocalStorage<string>("rememberMe", "");
    const { handleSubmit, register, setError, getValues, setValue, control } = useForm<LoginAuthenticatedData>({
        defaultValues: {
            email: userStatus.email,
            password: "",
        },
        resolver: yupResolver(legacyPasswordValidationSchema),
    });
    const authenticateUser = async (data: LoginAuthenticatedData) => {
        log.info("handle authenticate", data.email, "*****", data.rememberEmail);

        try {
            await auth(data.email, data.password);
            onAuthenticate();
            log.info("Manual authentication with success");
        } catch (err) {
            log.warn("Failed to login: ", err);
            if (err instanceof ForbiddenError) {
                setError("password", { type: "authenticate", message: t("Login.Authenticate.DoNotMatch") });
                return;
            }
            throw err;
        }
    };

    const handleChangeRememberEmail = (rememberMe: boolean) => {
        if (rememberMe) {
            storeRememberEmail(userStatus.email);
        } else {
            clearRememberMe();
        }
    };

    const handleResetPassword = () => {
        return navToResetPassword(userStatus);
    };

    return (
        <StyledAuthForm data-testid="form-authenticate" onSubmit={handleSubmit(authenticateUser)}>
            <h2>{t("Login.AccessClientZone")}</h2>
            <span className="login-desc-free">{t("Login.FreeAndSimple")}</span>
            <div>
                <ItsMeButton
                    label={t("ItsMe.ItsMeLabel")}
                    dataTestId="its_me_button-button-login"
                    onClick={handleItsmeLogin}
                />
                <OrSeparator />
            </div>
            <TextInput
                control={control}
                label={t("Login.Authenticate.YourEmail")}
                name="email"
                disabled
                defaultValue={getValues("email")}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <EmailIcon />
                        </InputAdornment>
                    ),
                }}
            />
            <PasswordInput
                control={control}
                label={t("Login.Authenticate.YourPassword")}
                autoFocus
                isNew={false}
                name="password"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setValue("password", event.target.value, { shouldValidate: true });
                }}
            />
            <br />
            {cookieConsentCategories.includes("functional") && (
                <FormControlLabel
                    name="rememberEmail"
                    control={
                        <Checkbox
                            color="primary"
                            checked={!!rememberEmail}
                            inputProps={{
                                // Necessary, waiting https://github.com/mui/material-ui/issues/34154 than remove
                                // @ts-ignore
                                "data-testid": "login_authenticate-checkbox-remember",
                            }}
                            onChange={(e) => handleChangeRememberEmail(e.target.checked)}
                        />
                    }
                    label={<p className="remember-me ">{t("Login.Authenticate.RememberMe")}</p>}
                    labelPlacement="end"
                    {...register("rememberEmail", {
                        required: false,
                    })}
                    // inputRef={}
                />
            )}

            <a
                className="forgot-password-label d-flex justify-center"
                data-testid="login_authenticate-link-forgot_password"
                onClick={handleResetPassword}
            >
                {t("Login.Authenticate.ForgotPassword")}
            </a>

            <Button type="submit" data-testid="login_authenticate-button-login" fullWidth>
                {t("Login.Authenticate.Login")}
            </Button>
        </StyledAuthForm>
    );
};

export default LoginAuthenticated;
